import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, RefreshCcw, Play, ExternalLink, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; } }
function dur(s) { if (s == null) return "—"; const n = Math.round(s); return n < 60 ? `${n}s` : `${Math.floor(n/60)}m ${n%60}s`; }

const STATUS_COLOR = {
  WAITING: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  ONLINE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  FAILED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  ROLLED_BACK: "bg-neutral-500/15 text-neutral-300 border-neutral-500/30",
};
function statusCls(s) {
  return STATUS_COLOR[s] || "bg-cyan-500/15 text-cyan-300 border-cyan-500/30 animate-pulse";
}

function SummaryCard({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "border-white/10 bg-white/5", emerald: "border-emerald-500/30 bg-emerald-500/10",
    amber: "border-amber-500/30 bg-amber-500/10", rose: "border-rose-500/30 bg-rose-500/10",
    cyan: "border-cyan-500/30 bg-cyan-500/10", neutral: "border-neutral-500/30 bg-neutral-500/10",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{label}</div>
      <div className="text-2xl font-black text-white mt-1">{value}</div>
    </div>
  );
}

function StartDialog({ onClose, tenants, domains, onStarted }) {
  const [tenantId, setTenantId] = useState((tenants[0] || {}).tenant_id || "");
  const [domainId, setDomainId] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checklist, setChecklist] = useState(null);

  const tenantDomains = domains.filter(d => d.tenant_id === tenantId);

  useEffect(() => {
    if (tenantId && domainId) {
      api.get("/platform/domain-binding/checklist", { params: { tenant_id: tenantId, domain_id: domainId } })
        .then(r => setChecklist(r.data)).catch(() => setChecklist(null));
    } else setChecklist(null);
  }, [tenantId, domainId]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/platform/domain-binding/start", {
        tenant_id: tenantId, domain_id: domainId, dry_run: dryRun,
      });
      toast.success(`Binding ${r.data.binding.status}`);
      onStarted?.(r.data.binding);
    } catch (e) { notifyError(e, "Gagal start binding"); }
    finally { setBusy(false); }
  };

  const fld = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-500/60";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="adb-start-dialog">
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#121D33] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={16} className="text-cyan-300" />
          <div className="text-white font-display font-black text-lg">Start Auto Domain Binding</div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Tenant</label>
            <select value={tenantId} onChange={(e) => { setTenantId(e.target.value); setDomainId(""); }} className={fld} data-testid="adb-tenant">
              <option value="">— Pilih tenant —</option>
              {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Domain</label>
            <select value={domainId} onChange={(e) => setDomainId(e.target.value)} className={fld} data-testid="adb-domain">
              <option value="">— Pilih domain —</option>
              {tenantDomains.map(d => <option key={d.id} value={d.id}>{d.fqdn}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="adb-dry-run" />
            Dry-run mode (safe — mock InfrastructureAdapter, tidak reload nginx VPS)
          </label>
          {checklist && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-3" data-testid="adb-checklist-preview">
              <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-2">
                Go Live Checklist ({checklist.passed}/{checklist.total})
              </div>
              <div className="space-y-1">
                {checklist.checks.map((c) => (
                  <div key={c.name} className="flex items-center gap-2 text-xs" data-testid={`adb-check-${c.name}`}>
                    {c.ok ? <CheckCircle2 size={12} className="text-emerald-400" /> : <XCircle size={12} className="text-rose-400" />}
                    <span className="font-bold uppercase text-[10px] w-[130px] text-white/80 tracking-wider">{c.name}</span>
                    <span className="text-white/60 font-mono">{c.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="adb-cancel">Batal</Button>
          <Button onClick={start} disabled={busy || !checklist?.ok} className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:opacity-90 text-white font-black" data-testid="adb-submit">
            <Play size={12} className="mr-1" /> {busy ? "Starting…" : "Start Binding"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BindingDetail({ binding, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const act = async (verb) => {
    setBusy(true);
    try {
      await api.post(`/platform/domain-binding/${binding.id}/${verb}`, {});
      toast.success(`${verb} ok`);
      onRefresh();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-3" data-testid={`adb-detail-${binding.id}`}>
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${statusCls(binding.status)}`} data-testid={`adb-status-${binding.status.toLowerCase()}`}>{binding.status}</span>
        <span className="text-xs font-mono text-white/70">{binding.id.slice(0, 12)}</span>
        <span className="text-xs text-white/60">step: {binding.current_step || "—"}</span>
        <span className="text-xs text-white/60">· {dur(binding.duration_seconds)}</span>
        <div className="ml-auto flex items-center gap-2">
          {binding.status === "FAILED" && <Button size="sm" onClick={() => act("retry")} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid={`adb-retry-${binding.id}`}>Retry</Button>}
          {["ONLINE", "FAILED"].includes(binding.status) && <Button size="sm" onClick={() => act("rollback")} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs" data-testid={`adb-rollback-${binding.id}`}>Rollback</Button>}
        </div>
      </div>
      {binding.error_message && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-2 mb-3 text-xs text-rose-200 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {binding.error_message}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Timeline ({binding.timeline?.length || 0})</div>
        <ol className="relative border-l border-white/10 ml-2 space-y-2 max-h-72 overflow-auto pr-2" data-testid={`adb-timeline-${binding.id}`}>
          {(binding.timeline || []).map((log, i) => (
            <li key={i} className="ml-3">
              <div className="absolute -left-[5px] w-2 h-2 rounded-full bg-cyan-400" />
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[10px] font-black uppercase tracking-widest text-white/70">{log.status}</span>
                {log.phase && <span className="text-[9px] uppercase tracking-widest text-emerald-300/70 font-bold">{log.phase}</span>}
                <span className="text-[9px] text-white/40 font-mono">{fmt(log.at)}</span>
              </div>
              <div className="text-xs text-white/80">{log.message}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

export default function PlatformDomainBinding() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [domains, setDomains] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ls, tns, ds] = await Promise.all([
        api.get("/platform/domain-binding"),
        api.get("/platform/tenants"),
        api.get("/platform/domains"),
      ]);
      setItems(ls.data.items || []);
      setSummary(ls.data.summary);
      setTenants(tns.data.items || []);
      setDomains(ds.data.items || []);
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const domainMap = Object.fromEntries(domains.map(d => [d.id, d]));
  const tenantMap = Object.fromEntries(tenants.map(t => [t.tenant_id, t]));

  return (
    <PlatformLayout title="Auto Domain Binding">
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="text-white font-display font-black text-xl">Auto Domain Binding & Website Activation</div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="adb-refresh"><RefreshCcw size={12} className="mr-1" /> Refresh</Button>
          <Button onClick={() => setDialogOpen(true)} className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:opacity-90 text-white font-black" data-testid="adb-start-btn">
            <Zap size={14} className="mr-1" /> Start Binding
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 mb-4">
          <SummaryCard label="Total" value={summary.total} tone="cyan" testid="adb-sum-total" />
          <SummaryCard label="Pending" value={summary.pending} tone="slate" testid="adb-sum-pending" />
          <SummaryCard label="Binding" value={summary.binding} tone="amber" testid="adb-sum-binding" />
          <SummaryCard label="Online" value={summary.success} tone="emerald" testid="adb-sum-success" />
          <SummaryCard label="Failed" value={summary.failed} tone="rose" testid="adb-sum-failed" />
          <SummaryCard label="Rolled Back" value={summary.rolled_back} tone="neutral" testid="adb-sum-rolled-back" />
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden mb-4">
        <table className="w-full text-sm" data-testid="adb-table">
          <thead className="bg-white/5 border-b border-white/10">
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/50">
              <th className="px-4 py-3 font-bold">Tenant</th>
              <th className="px-4 py-3 font-bold">Domain</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold">Current Step</th>
              <th className="px-4 py-3 font-bold">Duration</th>
              <th className="px-4 py-3 font-bold">Created</th>
              <th className="px-4 py-3 font-bold text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-white/40 text-center">Memuat…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-white/40 text-center" data-testid="adb-empty">Belum ada binding.</td></tr>}
            {!loading && items.map((b) => (
              <tr key={b.id} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setExpanded(prev => ({ ...prev, [b.id]: !prev[b.id] }))} data-testid={`adb-row-${b.id}`}>
                <td className="px-4 py-3 text-white/80">{tenantMap[b.tenant_id]?.tenant_name || b.tenant_id.slice(0, 8)}</td>
                <td className="px-4 py-3 font-mono text-white">{domainMap[b.domain_id]?.fqdn || b.domain_id.slice(0, 12)}</td>
                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${statusCls(b.status)}`}>{b.status}</span></td>
                <td className="px-4 py-3 text-xs text-white/60 font-mono">{b.current_step || "—"}</td>
                <td className="px-4 py-3 text-white/60">{dur(b.duration_seconds)}</td>
                <td className="px-4 py-3 text-white/60 text-xs">{fmt(b.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" className="text-white/60 hover:bg-white/10" data-testid={`adb-toggle-${b.id}`}>
                    {expanded[b.id] ? "Hide" : "Detail"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {items.filter(b => expanded[b.id]).map(b => <BindingDetail key={b.id} binding={b} onRefresh={load} />)}

      {dialogOpen && (
        <StartDialog
          onClose={() => setDialogOpen(false)}
          tenants={tenants}
          domains={domains}
          onStarted={() => { setDialogOpen(false); load(); }}
        />
      )}
    </PlatformLayout>
  );
}
